import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/**
 * Shows an inline spinner on a button while an operation runs and blocks further clicks,
 * so create/update/save/delete can't be double-submitted.
 *
 * Usage: <button mat-raised-button [hmtButtonLoading]="saving$ | async" (click)="save()">Save</button>
 *
 * Clicks are swallowed in the capture phase instead of toggling `disabled`, so the button's own
 * [disabled] binding is left untouched. Styles live in styles/overrides/_button-loading.scss.
 */
@Directive({
  selector: '[hmtButtonLoading]',
  standalone: false
})
export class ButtonLoadingDirective implements OnInit, OnDestroy {

  private loading = false;
  private spinner: HTMLElement;
  private removeClickGuard: () => void;

  @Input() set hmtButtonLoading(value: boolean | null) {
    this.loading = !!value;
    this.render();
  }

  constructor(private elementRef: ElementRef<HTMLElement>, private renderer: Renderer2) {
  }

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;
    this.spinner = this.renderer.createElement('span');
    this.renderer.addClass(this.spinner, 'hmt-button-spinner');
    this.renderer.setAttribute(this.spinner, 'aria-hidden', 'true');
    this.renderer.appendChild(host, this.spinner);

    const guard = (event: Event) => {
      if (this.loading) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    host.addEventListener('click', guard, true);
    this.removeClickGuard = () => host.removeEventListener('click', guard, true);
    this.render();
  }

  ngOnDestroy(): void {
    this.removeClickGuard?.();
  }

  private render(): void {
    const host = this.elementRef.nativeElement;
    if (this.loading) {
      this.renderer.addClass(host, 'hmt-button-loading');
      this.renderer.setAttribute(host, 'aria-busy', 'true');
    } else {
      this.renderer.removeClass(host, 'hmt-button-loading');
      this.renderer.removeAttribute(host, 'aria-busy');
    }
  }
}
